import { useEffect, useId, type CSSProperties, type RefObject } from 'react'
import { Icon } from '@iconify/react'
import {
  purchaseScratchPrizeLabel,
  type PurchaseScratchAttempt,
} from '@/lib/purchase-scratch'
import styles from '@/components/PurchaseScratchCard/PurchaseScratchCard.module.css'

export function PurchaseScratchResult({
  card,
  dialogRef,
  celebrate,
  busy,
  error,
  salesOpen,
  hasNext,
  canDonate,
  onClose,
  onUseCoupon,
  onViewNumbers,
  onNext,
  onDonate,
}: {
  card: PurchaseScratchAttempt
  dialogRef: RefObject<HTMLDialogElement | null>
  celebrate: boolean
  busy: boolean
  error: string
  salesOpen: boolean
  hasNext: boolean
  canDonate: boolean
  onClose: () => void
  onUseCoupon: () => void
  onViewNumbers: () => void
  onNext: () => void
  onDonate: () => void
}) {
  const id = useId()
  const prize = card.prize
  const canceled = prize.status === 'canceled'
  const won = card.won
  const title = canceled ? 'Prêmio cancelado' : won ? 'Parabéns!' : 'Não foi dessa vez'
  useEffect(() => {
    const element = dialogRef.current
    const previous =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    element?.showModal()
    return () => {
      const wasOpen = element?.open
      element?.close()
      if (wasOpen) previous?.focus({ preventScroll: true })
    }
  }, [dialogRef])
  return (
    <dialog
      ref={dialogRef}
      className={`${styles.modal} ${styles.resultModal}`}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      onCancel={(event) => {
        event.preventDefault()
        event.stopPropagation()
        if (!busy) onClose()
      }}
    >
      <div className={styles.modalBody}>
        {celebrate && won && !canceled && (
          <div className={styles.confetti} aria-hidden>
            {Array.from({ length: 24 }, (_, i) => (
              <i key={i} style={{ '--n': i } as CSSProperties} />
            ))}
          </div>
        )}
        <button
          type="button"
          className={styles.close}
          style={{ marginLeft: 'auto' }}
          aria-label="Fechar resultado da raspadinha"
          onClick={onClose}
          disabled={busy}
        >
          <Icon icon="mdi:close" />
        </button>
        <div className={`${styles.reward} ${!won ? styles.noPrize : ''}`}>
          <Icon
            icon={
              canceled
                ? 'mdi:ticket-remove-outline'
                : !won
                  ? 'mdi:star-four-points-outline'
                  : prize.prize_type === 'coupon'
                    ? 'mdi:ticket-percent-outline'
                    : 'mdi:gift-outline'
            }
          />
          <span className={styles.eyebrow}>
            {won ? 'RESULTADO DA SUA RASPADINHA' : 'OBRIGADO POR PARTICIPAR'}
          </span>
          <h2 id={`${id}-title`}>{title}</h2>
          <p id={`${id}-description`}>
            {!won
              ? 'Esta raspadinha terminou sem prêmio.'
              : 'Confira o resultado e a situação do seu prêmio.'}
          </p>
          <strong className={styles.resultValue}>
            {won ? purchaseScratchPrizeLabel(card) : 'A sorte continua'}
          </strong>
          <p>
            {canceled
              ? 'Este prêmio foi cancelado porque a compra de origem foi revertida.'
              : !won
                ? 'Sua participação está salva no histórico de raspadinhas.'
                : prize.status === 'used'
                  ? prize.prize_type === 'coupon'
                    ? 'Este cupom já foi utilizado.'
                    : 'Seus números já foram resgatados e estão ativos.'
                  : prize.prize_type === 'coupon'
                    ? 'Seu cupom está guardado. Use em uma nova compra nesta campanha.'
                    : 'Seus números estão reservados e esperando por você na aba Números.'}
          </p>
          {won && prize.prize_type === 'coupon' && prize.prize_payload?.code && (
            <code>{prize.prize_payload.code}</code>
          )}
          {won &&
            prize.prize_type === 'lucky_number' &&
            Boolean(prize.prize_payload?.lucky_numbers?.length) && (
              <p className={styles.resultNumbers}>
                {prize.prize_payload?.lucky_numbers?.join(' · ')}
              </p>
            )}
          <p className={styles.receipt}>
            Raspadinha #{card.id} · Compra #{card.payment_id}
          </p>
          <div className={styles.modalFooter}>
            {won &&
              prize.status === 'pending' &&
              (prize.prize_type === 'coupon' ? (
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={busy || !salesOpen}
                  onClick={onUseCoupon}
                >
                  {busy ? 'Abrindo compra…' : 'Usar agora'}
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.primaryButton}
                  disabled={busy}
                  onClick={onViewNumbers}
                >
                  Resgatar agora
                </button>
              ))}
            {hasNext && (
              <button
                type="button"
                className={styles.quietButton}
                disabled={busy}
                onClick={onNext}
              >
                Raspar próxima raspadinha
              </button>
            )}
            {!hasNext && canDonate && (
              <button
                type="button"
                className={styles.primaryButton}
                disabled={busy}
                onClick={onDonate}
              >
                Doar e ganhar uma raspadinha
              </button>
            )}
            <button
              type="button"
              className={won ? styles.quietButton : styles.primaryButton}
              disabled={busy}
              onClick={onClose}
              autoFocus
            >
              Fechar
            </button>
          </div>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </div>
      </div>
    </dialog>
  )
}
